import { createGeoJsonAdapter } from '@nextgis/ngw-kit';

import type {
  Style3DFieldDefinition,
  Style3DModel,
} from './createStyle3DAdapter';
import type { GetClassAdapterOptions } from '@nextgis/ngw-kit';
import type { LngLatBoundsArray, Type } from '@nextgis/utils';
import type {
  GeoJsonAdapterOptions,
  MainLayerAdapter,
  Model3DOptions,
  VectorLayerAdapter,
} from '@nextgis/webmap';
import type { CompositeRead } from '@nextgisweb/resource/type/api';
import type { Feature, FeatureCollection, Point } from 'geojson';

export async function createModel3DAdapter(
  vectorItem: CompositeRead,
  style: Style3DModel,
  options: GetClassAdapterOptions,
): Promise<Type<MainLayerAdapter<any, any, GeoJsonAdapterOptions>>> {
  const Model3DAdapter = options.webMap.mapAdapter.layerAdapters['MODEL_3D'];
  const fields = vectorItem.feature_layer?.fields || [];
  class Model3dAdapter
    implements VectorLayerAdapter<any, any, GeoJsonAdapterOptions>
  {
    options = {};
    map: any = options.webMap.mapAdapter.map;
    private _extent?: LngLatBoundsArray;
    private _models: MainLayerAdapter[] = [];
    private _visible = false;

    async addLayer(opt: GeoJsonAdapterOptions) {
      return this._models;
    }

    showLayer() {
      this._visible = true;
      this._models.forEach((x) => x.showLayer && x.showLayer());
    }

    hideLayer() {
      this._visible = false;
      this._models.forEach((x) => x.hideLayer && x.hideLayer());
    }

    getBounds(): LngLatBoundsArray | undefined {
      return this._extent;
    }

    async setData(data: FeatureCollection) {
      const centroid = await import('@turf/centroid');
      const bbox = await import('@turf/bbox');
      this._extent = bbox.default(data);

      for (const x of data.features) {
        const center: Feature<Point> = centroid.default(x as any);
        const [lon, lat] = center.geometry.coordinates;
        if (x.properties) {
          const model = this._getProperty(style.id, x);
          if (model) {
            // const height = this._getProperty(style.heightField, x) as number;
            const rotate = this._getProperty(style.rotate, x) as number;
            const scale = this._getProperty(style.scale, x) as number;
            const url =
              options.connector.options.baseUrl +
              `/api/component/model_3d/${model}/data.glb`;
            await this._addModel({ url, lon, lat, rotate, scale });
          }
        }
      }
    }

    _removeModels() {
      this._models.forEach((x) => {
        if (x.removeLayer) {
          x.removeLayer();
        }
      });
      this._models = [];
    }

    _getProperty(
      prop: Style3DFieldDefinition,
      feature: Feature,
    ): string | number | boolean | undefined {
      if (prop.type === 'VALUE') {
        return prop.value;
      }
      const field = fields.find((x) => x.id === Number(prop.field));
      if (field) {
        return feature.properties && feature.properties[field.keyname];
      }
    }

    async _addModel(opt: Model3DOptions) {
      const modelAdapter = new Model3DAdapter(this.map);
      await modelAdapter.addLayer(opt);
      this._models.push(modelAdapter);
      if (this._visible && modelAdapter.showLayer) {
        modelAdapter.showLayer();
      }
    }
  }

  const geojsonOptions: GetClassAdapterOptions = {
    ...options,
    layerOptions: {
      ...options.layerOptions,
      adapterOptions: {},
      resource: vectorItem.resource.id,
    },
    item: vectorItem,
    Adapter: Model3dAdapter,
  };

  const GeojsonAdapter = await createGeoJsonAdapter(geojsonOptions);

  return GeojsonAdapter;
}
